// smartFetch — обёртка над native fetch с двумя таймаутами:
// 1) TTFB-таймаут (default 5s) ловит «тихий дроп» на пути (РКН-кейс:
//    TCP открыт, пакеты RST не приходят, fetch висит десятки секунд).
// 2) Total-таймаут (default 15s) — финальный safeguard для медленной сети.
//
// Сигнатура совместима с globalThis.fetch — потому что используется как
// `fetch` provider в openapi-fetch (см. app/javascript/api/client.ts).
//
// AbortError-причина мапится в SmartFetchError с reason — вызывающий код
// по reason понимает, что это сетевая проблема (ttfb/overall/network) vs
// штатная отмена юзером (user — обычно от useApiRequest при race/unmount).

export type SmartFetchAbortReason = 'ttfb' | 'overall' | 'user' | 'network'

export class SmartFetchError extends Error {
  constructor(
    public reason: SmartFetchAbortReason,
    public originalError?: unknown,
  ) {
    super(`smartFetch failed: ${reason}`)
    this.name = 'SmartFetchError'
  }
}

export interface SmartFetchInit extends RequestInit {
  ttfbTimeoutMs?: number   // default 5000
  totalTimeoutMs?: number  // default 15000
}

const DEFAULT_TTFB_MS = 5000
const DEFAULT_TOTAL_MS = 15000

export async function smartFetch(
  input: RequestInfo | URL,
  init: SmartFetchInit = {},
): Promise<Response> {
  const {
    ttfbTimeoutMs = DEFAULT_TTFB_MS,
    totalTimeoutMs = DEFAULT_TOTAL_MS,
    signal: userSignalFromInit,
    ...restInit
  } = init

  const ttfbCtrl = new AbortController()
  const totalCtrl = new AbortController()
  let firstByteReceived = false
  let abortReason: Exclude<SmartFetchAbortReason, 'network'> | null = null

  const ttfbTimer = setTimeout(() => {
    if (!firstByteReceived) {
      abortReason = 'ttfb'
      ttfbCtrl.abort()
    }
  }, ttfbTimeoutMs)

  const totalTimer = setTimeout(() => {
    abortReason = 'overall'
    totalCtrl.abort()
  }, totalTimeoutMs)

  // Юзерский signal приходит либо через init.signal (наш вызов smartFetch(url, { signal })),
  // либо внутри Request (когда openapi-fetch передаёт готовый Request объект).
  const userSignal =
    userSignalFromInit ?? (input instanceof Request ? input.signal : null) ?? null

  const signals: AbortSignal[] = [ttfbCtrl.signal, totalCtrl.signal]
  if (userSignal) signals.push(userSignal)
  const combinedSignal = AbortSignal.any(signals)

  try {
    const response = await fetch(input, { ...restInit, signal: combinedSignal })
    firstByteReceived = true
    return response
  } catch (e) {
    // abortReason мутируется из setTimeout-замыканий — TS control-flow этого
    // не видит и считает значение «always falsy».
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (abortReason) throw new SmartFetchError(abortReason, e)
    if (userSignal?.aborted) throw new SmartFetchError('user', e)
    throw new SmartFetchError('network', e)
  } finally {
    clearTimeout(ttfbTimer)
    clearTimeout(totalTimer)
  }
}
